import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getSeoForPath, SITE_NAME, TWITTER_HANDLE } from '../../seo/seoConfig';

function upsertMetaTag(selector, attributeName, attributeValue, contentValue) {
  let meta = document.head.querySelector(selector);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attributeName, attributeValue);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', contentValue);
}

function upsertCanonicalLink(hrefValue) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', hrefValue);
}

function upsertStructuredData(data) {
  const scriptId = 'route-structured-data';
  let script = document.head.querySelector(`#${scriptId}`);

  if (!data) {
    script?.remove();
    return;
  }

  if (!script) {
    script = document.createElement('script');
    script.setAttribute('id', scriptId);
    script.setAttribute('type', 'application/ld+json');
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(data);
}

const SeoManager = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = getSeoForPath(pathname);
    document.title = seo.title || SITE_NAME;

    upsertMetaTag('meta[name="description"]', 'name', 'description', seo.description);
    upsertMetaTag(
      'meta[name="robots"]',
      'name',
      'robots',
      seo.indexable
        ? 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
        : 'noindex, nofollow'
    );
    upsertMetaTag('meta[name="keywords"]', 'name', 'keywords', (seo.keywords || []).join(', '));
    upsertMetaTag('meta[property="og:site_name"]', 'property', 'og:site_name', SITE_NAME);
    upsertMetaTag('meta[property="og:type"]', 'property', 'og:type', seo.pageType === 'CreativeWork' ? 'article' : 'website');
    upsertMetaTag('meta[property="og:title"]', 'property', 'og:title', seo.title);
    upsertMetaTag('meta[property="og:description"]', 'property', 'og:description', seo.description);
    upsertMetaTag('meta[property="og:url"]', 'property', 'og:url', seo.canonicalUrl);
    upsertMetaTag('meta[property="og:image"]', 'property', 'og:image', seo.ogImage);
    upsertMetaTag('meta[property="og:image:alt"]', 'property', 'og:image:alt', seo.ogImageAlt);
    upsertMetaTag('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    upsertMetaTag('meta[name="twitter:site"]', 'name', 'twitter:site', TWITTER_HANDLE);
    upsertMetaTag('meta[name="twitter:creator"]', 'name', 'twitter:creator', TWITTER_HANDLE);
    upsertMetaTag('meta[name="twitter:title"]', 'name', 'twitter:title', seo.title);
    upsertMetaTag('meta[name="twitter:description"]', 'name', 'twitter:description', seo.description);
    upsertMetaTag('meta[name="twitter:image"]', 'name', 'twitter:image', seo.ogImage);
    upsertMetaTag('meta[name="twitter:image:alt"]', 'name', 'twitter:image:alt', seo.ogImageAlt);
    upsertMetaTag('meta[name="twitter:url"]', 'name', 'twitter:url', seo.canonicalUrl);

    upsertCanonicalLink(seo.canonicalUrl);
    upsertStructuredData(seo.structuredData);
  }, [pathname]);

  return null;
};

export default SeoManager;
